import React, { useState } from "react";
import { makeGet, makeDelete } from "@culturehq/client";
import { Loader, Pagination, Table, Warning, Badge, Icon, PlainButton, Panel, SearchBar, Dropdown, Tag } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination } from "app/hooks";
import { StoryTrendLink } from "app/links";
import { isTarget, t, Trans } from "app/utils";

import { AiBookIcon } from "components/common/icons";
import AccordianRow from "components/common/AccordianRow";
import PageHeader from "components/display/PageHeader";
import { getIcon } from "constants/tagsTypes";
import QuickOptions from "components/display/QuickOptions";
import AnswerPrompts from "components/stories/AnswerPrompts";
import PromptShareOptions from "components/stories/PromptShareOptions";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import { createEntity, updateEntity, updateChildEntity, deleteEntity, replaceEntity, deleteChildEntity } from "./mutations";
import NewStoryQuestionModal from "./story-questions/NewStoryQuestionModal";
import OldAndChildStoryQuestionQuickOptions from "./OldAndChildStoryQuestionQuickOptions";
import StoryQuestionModal from "./story-questions/StoryQuestionModal";
import DuplicateStoryQuestionOption from "./story-questions/DuplicateStoryQuestionOption";
import NewAIStoryQuestionModal from "./story-questions/NewAIStoryQuestionModal";

const createStoryQuestion = createEntity("storyQuestions", "question");
const updateStoryQuestion = updateEntity("storyQuestions");
const updateChildStoryQuestion = updateChildEntity("storyQuestions");
const replaceStoryQuestion = replaceEntity("storyQuestions");
const deleteStoryQuestion = deleteEntity("storyQuestions");
const deleteChildStoryQuestion = deleteChildEntity("storyQuestions");


const StoryQuestionRow = ({
  storyQuestion,
  profile,
  onCreate,
  onUpdate,
  onDelete,
  onReplace,
  onAccept
}) => (
  <tr>
    <td />
    <td className="Story-Question__Title">
      {storyQuestion?.status === "archived" ? (
        <StoryTrendLink className="Disabled-link" storyQuestion={storyQuestion}>{storyQuestion.question}</StoryTrendLink>
      ) : (
        <StoryTrendLink storyQuestion={storyQuestion}>{storyQuestion.question}</StoryTrendLink>
      )}
      {storyQuestion?.visibility === "public_access" && (
        <Tag className="ChildPrompts__Tag" color="#6a89af"><Trans>Public</Trans></Tag>
      )}
      {storyQuestion?.status === "paused" && (
        <Tag className="ChildPrompts__Tag" color="red"><Trans>Paused</Trans></Tag>
      )}
      {storyQuestion?.status === "hidden" && (
        <Tag className="ChildPrompts__Tag" color="red"><Trans>Hidden</Trans></Tag>
      )}
      {storyQuestion?.status === "archived" && (
        <Tag className="ChildPrompts__Tag" color="red"><Trans>Archived</Trans></Tag>
      )}
      {storyQuestion?.storyQuestionType === "ai_prompt" && (
        <Tag className="ChildPrompts__Tag chq-tag-bk"><Trans>AI</Trans></Tag>
      )}
    </td>
    <td className="Answers-column">
      {storyQuestion.answers}
    </td>
    <td>
      <div className="chq-ffd-tags">
        {(storyQuestion.storyQuestionTags.length > 0)
          ? storyQuestion.storyQuestionTags.map(tag => tag.entity && (
            <Badge key={tag.id}>
              <Icon icon={getIcon(tag.entityType)} className="category-icon" />
              {tag.entity.name}
            </Badge>
          )) : (
            <Badge key="everyone">
              <Icon icon="ios-people-outline" className="category-icon" />
              Everyone
            </Badge>
          )}
      </div>
    </td>
    <td className="Actions-column">
      <OldAndChildStoryQuestionQuickOptions
        storyQuestion={storyQuestion}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onReplace={onReplace}
        onDelete={onDelete}
        onAccept={onAccept}
      />
      <PromptShareOptions
        organization={profile.organization}
        storyQuestion={storyQuestion}
        parentPromptId={storyQuestion.parentStoryQuestion?.id}
      />
    </td>
  </tr>
);

const StoryQuestionAccordianRow = ({
  storyQuestion,
  profile,
  onCreate,
  onUpdate,
  onDelete,
  onReplace,
  onAccept
}) => (
  <AccordianRow upDown>
    <>
      <td className="Story-Question__Title">
        {storyQuestion?.status === "archived" ? (
          <StoryTrendLink className="Disabled-link" storyQuestion={storyQuestion}>{storyQuestion.question}</StoryTrendLink>
        ) : (
          <StoryTrendLink storyQuestion={storyQuestion}>{storyQuestion.question}</StoryTrendLink>
        )}
        {storyQuestion?.visibility === "public_access" && (
          <Tag className="ChildPrompts__Tag" color="#6a89af"><Trans>Public</Trans></Tag>
        )}
        {storyQuestion?.status === "paused" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Paused</Trans></Tag>
        )}
        {storyQuestion?.status === "hidden" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Hidden</Trans></Tag>
        )}
        {storyQuestion?.status === "archived" && (
          <Tag className="ChildPrompts__Tag" color="red"><Trans>Archived</Trans></Tag>
        )}
        {storyQuestion?.storyQuestionType === "ai_prompt" && (
          <Tag className="ChildPrompts__Tag chq-tag-bk"><Trans>AI</Trans></Tag>
        )}
      </td>
      <td className="Answers-column">
        {storyQuestion.answers}
      </td>
      <td>
        <div className="chq-ffd-tags">
          {(storyQuestion.storyQuestionTags.length > 0)
            ? storyQuestion.storyQuestionTags.map(tag => tag.entity && (
              <Badge key={tag.id}>
                <Icon icon={getIcon(tag.entityType)} className="category-icon" />
                {tag.entity.name}
              </Badge>
            )) : (
              <Badge key="everyone">
                <Icon icon="ios-people-outline" className="category-icon" />
                Everyone
              </Badge>
            )}
        </div>
      </td>
      <td className="Actions-column">
        <QuickOptions>
          <ul className="dropdown-menu">
            <li>
              <div
                onClick={event => {
                  event.stopPropagation();
                }}
              >
                <NewStoryQuestionModal
                  storyQuestion={storyQuestion}
                  onUpdate={onUpdate}
                  selectedOptions={storyQuestion.storyQuestionTags}
                >
                  {onTrigger => (
                    <PlainButton
                      onClick={event => {
                        event.stopPropagation();
                        onTrigger();
                      }}
                    >
                      <Icon icon="edit-outlined" />
                      <Trans>Edit</Trans>
                    </PlainButton>
                  )}
                </NewStoryQuestionModal>
              </div>
            </li>
            <li>
              <DuplicateStoryQuestionOption type="trend" storyQuestion={storyQuestion} onCreate={onCreate} />
            </li>
            <li>
              <ChqModalConfirmDelete
                onAccept={() => onAccept(storyQuestion)}
                trigger={onTrigger => (
                  <PlainButton
                    onClick={event => {
                      event.stopPropagation();
                      onTrigger();
                    }}
                  >
                    <Icon icon="trash-a-outlined" />
                    <Trans>Delete</Trans>
                  </PlainButton>
                )}
              >
                <p><Trans>Are you sure you would like to delete this story prompt?</Trans></p>
                <p>
                  <Trans>
                    All the stories that belong to this story prompt will be deleted too
                  </Trans>
                </p>
              </ChqModalConfirmDelete>
            </li>
          </ul>
        </QuickOptions>
        <PromptShareOptions
          organization={profile.organization}
          storyQuestion={storyQuestion}
          parentPromptId={storyQuestion.parentStoryQuestion?.id}
        />
      </td>
    </>
    <>
      <td />
      <td colSpan="4">
        <AnswerPrompts
          childStoryQuestions={storyQuestion.childStoryQuestions}
          parentId={storyQuestion.id}
          profile={profile}
          onPosted={() => {}}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onReplace={onReplace}
          onDelete={onDelete}
          onAccept={onAccept}
        />
      </td>
    </>
  </AccordianRow>
);

const CreatePromptOption = ({ description, icon, Modal, onCreate, text }) => {
  const iconMap = {
    "ai-book": <AiBookIcon />
  };

  return (
    <Dropdown.Option className="Dropdown-Option" value="">
      <Modal onCreate={onCreate}>
        {onTrigger => (
          <div className="create-trend-button" onClick={onTrigger}>
            <div className="Dropdown-Option__MainText">
              {iconMap[icon] || <Icon icon={icon} />}
              {text}
            </div>
            <div className="description-prompts">
              <span>{description}</span>
            </div>
          </div>
        )}
      </Modal>
    </Dropdown.Option>
  );
};

const MainActionButtons = ({ onCreate, organization }) => (
  <div className="main-action-button-container">
    <Dropdown className="CreatePromptDropdown" onChange={() => {}}>
      <Dropdown.Button className="new-prompt">
        <Icon icon="plus" primary />
        <span className="CreatePromptDropdown-Text"><Trans>Create New Prompt/Trend</Trans></span>
      </Dropdown.Button>
      <Dropdown.ListBox className="CreatePromptDropdown-Options">
        <div className="boolean-field-option">
          <CreatePromptOption
            description={t("A single question or statement")}
            icon="book"
            Modal={StoryQuestionModal}
            onCreate={onCreate}
            text={t("Create Story Prompt")}
          />
          <CreatePromptOption
            description={t("A curated page with multiple prompts")}
            icon="open-book"
            Modal={NewStoryQuestionModal}
            onCreate={onCreate}
            text={t("Create Story Trend")}
          />
          {!isTarget(organization.name) && (
            <CreatePromptOption
              description={t("A single question with AI help")}
              icon="ai-book"
              Modal={NewAIStoryQuestionModal}
              onCreate={onCreate}
              text={t("AI Story Prompt")}
            />
          )}
        </div>
      </Dropdown.ListBox>
    </Dropdown>
  </div>
);

const StoryQuestion = () => {
  const { profile } = useProfile();
  const [page, onPageClick] = usePagination();
  const [query, setQuery] = useState(undefined);
  const {
    fetching,
    json: { storyQuestions = [], pagination = {} },
    onUpdate: onStoryQuestionUpdate
  } = useFetch(
    () => makeGet("/story_questions", { page: page || 1, query }),
    [page, query]
  );

  const handleSearch = searchQuery => {
    onPageClick(1);
    setQuery(searchQuery);
  };

  const onCreate = storyQuestion => onStoryQuestionUpdate(createStoryQuestion(storyQuestion));
  const onUpdate = storyQuestion => {
    const updateStrategy = storyQuestion.parentStoryQuestion
      ? updateChildStoryQuestion
      : updateStoryQuestion;
    onStoryQuestionUpdate(updateStrategy(storyQuestion));
  };
  const onReplace = (storyQuestion, id) => onStoryQuestionUpdate(
    replaceStoryQuestion(storyQuestion, id)
  );
  const onDelete = storyQuestion => {
    const deleteStrategy = storyQuestion.parentStoryQuestion
      ? deleteChildStoryQuestion
      : deleteStoryQuestion;
    onStoryQuestionUpdate(deleteStrategy(storyQuestion));
  };
  const onAccept = async storyQuestion => {
    await makeDelete(`/story_questions/${storyQuestion.id}`);
    onDelete(storyQuestion);
  };

  return (
    <Panel limitWidth>
      <Panel.Heading primary>
        <Trans>Story Prompts</Trans>
      </Panel.Heading>
      <Panel.Body>
        <div className="Story-Question padding-left-15px padding-right-15px">
          <MainActionButtons onCreate={onCreate} organization={profile.organization} />
          <PageHeader title={t("Manage Story Trends")}>
            <Trans>
              The below is a list of story trends that users and admins have created. They
              will appear in a preset list for users to choose from when they are adding a new
              story. You can also send prompts to specific people or groups to collect stories.
            </Trans>
          </PageHeader>
          <SearchBar placeholder="Search prompt" throttle={500} onSearch={handleSearch} />
          <Loader loading={fetching}>
            <Table>
              <thead>
                <tr>
                  <th />
                  <th>
                    <Trans>Title</Trans>
                  </th>
                  <th>
                    <Trans>Stories</Trans>
                  </th>
                  <th>
                    <Trans>Default Tags</Trans>
                  </th>
                  <th className="actions">
                    <Trans>Actions</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {storyQuestions.map(question => (
                  question.storyQuestionType === "parent_prompt" ? (
                    <StoryQuestionAccordianRow
                      key={question.id}
                      storyQuestion={question}
                      profile={profile}
                      onCreate={onCreate}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onReplace={onReplace}
                      onAccept={onAccept}
                    />
                  ) : (
                    <StoryQuestionRow
                      key={question.id}
                      storyQuestion={question}
                      profile={profile}
                      onCreate={onCreate}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onReplace={onReplace}
                      onAccept={onAccept}
                    />
                  )
                ))}
              </tbody>
            </Table>
            {storyQuestions.length < 1 && (
              <Warning>
                <Trans>
                  <strong>Uh oh!</strong> No results to show.
                </Trans>
              </Warning>
            )}
            <Pagination {...pagination} onClick={onPageClick} />
          </Loader>
        </div>
      </Panel.Body>
    </Panel>
  );
};

export default StoryQuestion;
